import { createRoute } from './khartoum/route.js';
import { createMaterials } from './khartoum/theme.js';
import { buildRoad } from './khartoum/road.js';
import { buildCity } from './khartoum/city.js';
import { buildStations } from './khartoum/stations.js';
import { buildJuiceShop } from './khartoum/juiceShop.js';
import { buildGantrySigns } from './khartoum/signs.js';
import { buildTeaStands } from './khartoum/teaStands.js';
import { buildTraffic } from './khartoum/traffic.js';
import { buildPoliceCheckpoints } from './khartoum/police.js';
import { buildClosedWorld } from './khartoum/closedWorld.js';
import { buildHitchhikers } from './khartoum/hitchhikers.js';
import { buildDroneAttack } from './khartoum/droneAttack.js';
import { buildGraffiti } from './khartoum/graffiti.js';
import { buildPetrolStands } from './khartoum/petrol.js';
import { STATION_QUEUE } from './khartoum/trafficFlow.js';

/**
 * Bashir Elnifidi Street (Street 60), Khartoum.
 *
 * The centreline and both fuel stations come from OpenStreetMap/OSRM. The
 * street is built at 1:1 metric scale from the Wad Madani highway in the south
 * to Al-Mashtal Street in the north.
 */
export const khartoumLevel = {
  name: 'Bashir Elnifidi Street',
  // Filled in by the builder below; the placeholder is only used before the
  // scene is assembled.
  spawn: { position: [0, 0.9, 0], yaw: 0 },
  build: buildKhartoum,
};

function buildKhartoum(level) {
  const route = createRoute();
  const materials = createMaterials(level.renderer);

  buildRoad(level, route, materials);
  const city = buildCity(level, route, materials);
  const juiceShop = buildJuiceShop(level, route);
  const stations = buildStations(level, route, materials);
  const signs = buildGantrySigns(level, route);
  const teaStands = buildTeaStands(level, route);
  // The petrol sellers set up past the station exits, so they need the station
  // positions only — not the station geometry.
  const petrolStands = buildPetrolStands(level, route);
  const closedWorld = buildClosedWorld(level, route);
  const hitchhikers = buildHitchhikers(level, route);
  const traffic = buildTraffic(level, route, materials, stations, hitchhikers);
  const police = buildPoliceCheckpoints(level, route);
  const droneAttack = buildDroneAttack(level);
  // Murals go on after the city so they can hang on its real wall segments.
  const graffiti = buildGraffiti(level, route, city.walls);

  // Start just south of the Bashair queue, facing north up Street 60. The long
  // line of idling cars and the station canopy are the opening shot.
  const spawnFrame = route.offsetFrame(1040, 4.5);
  level.spawn.set(spawnFrame.position.x, 1.05, spawnFrame.position.z);
  level.spawnYaw = spawnFrame.yaw;

  level.route = route;
  level.city = city;
  level.juiceShop = juiceShop;
  level.stations = stations;
  level.signs = signs;
  level.teaStands = teaStands;
  level.petrolStands = petrolStands;
  level.closedWorld = closedWorld;
  level.traffic = traffic;
  level.police = police;
  level.hitchhikers = hitchhikers;
  level.droneAttack = droneAttack;
  level.graffiti = graffiti;
  level.gasLineZones = buildGasLineZones(route, stations);
  level.onUpdate = (dt, player) => {
    traffic.update(dt, player);
    police.update(dt, player);
    teaStands.update(dt);
    hitchhikers.update(dt, player);
    droneAttack.update(dt, player);
  };
}

/**
 * The stretch of road where the fuel queue stands — "the gas line".
 *
 * The crows gather along it, so the trigger zone follows exactly the same
 * geometry as the traffic queue: two files of cars between the back of the
 * queue and the stop line. The samples are spaced along the queue so the
 * nearest point can be found quickly as the truck approaches.
 */
function buildGasLineZones(route, stations) {
  const STEP = 15;
  const zones = [];

  for (const station of stations) {
    const samples = [];
    const start = station.s - STATION_QUEUE.tail;
    const end = station.s - STATION_QUEUE.head;

    for (let s = start; s <= end; s += STEP) {
      for (const offset of STATION_QUEUE.lineOffsets) {
        const frame = route.offsetFrame(s, offset);
        samples.push(frame.position.clone());
      }
    }

    // The middle of the queue, used to work out which side the birds are on.
    const middle = route.offsetFrame((start + end) / 2, STATION_QUEUE.lineOffsets[0]);

    zones.push({
      id: station.id,
      station,
      samples,
      center: middle.position.clone(),
      right: middle.right.clone(),
    });
  }

  return zones;
}
